import { Text } from '@/components/ui/text'
import { api } from '@api'
import { AppTooltip } from '@components/AppTooltip'
import { Rank } from '@components/Rank'
import { createQuery } from '@tanstack/solid-query'
import dayjs from 'dayjs'
import type { JSXElement } from 'solid-js'
import { Show } from 'solid-js'
// Author.tsx
import { css } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'
import { Avatar } from '../features/Avatar'

type AuthorProfile = { username: string | null; rank?: string } // type this

const RANK_COLORS: Record<string, string> = {
  recruit: '#9aa0ad',
  rifleman: '#5b9bd5',
  lance_corporal: '#4a8fc7',
  corporal: '#3d7dbf',
  sergeant: '#d6a012',
  staff_sergant: '#c28f0f',
  second_lieutenant: '#4fae7a',
  full_lieutenant: '#2fa878',
  captain: '#12b886',
  commander: '#7c5cd6',
  admin: '#d94343',
}
const rankColor = (role: string) => RANK_COLORS[role.toLowerCase()] ?? '#9aa0ad'

async function fetchAuthorProfile(id: string): Promise<AuthorProfile> {
  const sb = api()

  // Public username (+ public role as fallback)
  const { data: pub, error: pubErr } = await sb
    .from('profile')
    .select('username')
    .eq('id', id)
    .maybeSingle()

  if (pubErr) throw pubErr

  // Private role (preferred for "rank" if present and RLS allows)
  const { data: priv, error: privErr } = await sb
    .from('profile_private')
    .select('role')
    .eq('id', id)
    .maybeSingle()

  // It's fine if privErr exists due to RLS; we'll just ignore and fallback.

  return { username: pub?.username ?? null, rank: priv?.role }
}

interface SetupAuthorProps {
  createdBy?: string
  created_at?: string
  gap?: number
  avatarSize?: string
  rankSize?: string
  nameFontSize?: string
  dateFontSize?: string
  metaSlot?: JSXElement
}

export default function SetupAuthor(props: SetupAuthorProps) {
  const authorQuery = createQuery(() => ({
    queryKey: ['author', props.createdBy],
    queryFn: () => fetchAuthorProfile(props.createdBy!),
    enabled: !!props.createdBy,
    // Cache for 1 day (24 hours)
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 1,
  }))

  const username = () =>
    authorQuery.data?.username ?? (props.createdBy ? 'Unknown' : '—')
  const rank = () => authorQuery.data?.rank ?? 'Unranked'

  return (
    <Flex
      alignItems="center"
      gap={props.gap ?? 4}
      minWidth={0}
    >
      <Box
        pos="relative"
        flexShrink={0}
        width={props.avatarSize || '36px'}
        height={props.avatarSize || '36px'}
      >
        <Avatar id={props.createdBy} />
        <Box
          pos="absolute"
          bottom='-3px'
          right='-3px'
          borderRadius='full'
          bg='gray.surface.bg'
          boxShadow='0 0 0 1px var(--colors-editorial-hair-strong)'
        >
          <AppTooltip
            content={rank()}
            positioning={{ placement: 'top' }}
          >
            <Rank
              dimensions={props.rankSize || '17px'}
              rank={rank()}
            />
          </AppTooltip>
        </Box>
      </Box>

      <Grid
        gap={0}
        minWidth={0}
      >
        <Flex
          alignItems="center"
          gap='7px'
          flexWrap='wrap'
        >
          <Text
            color="editorialInk"
            fontSize={props.nameFontSize || '13px'}
            fontWeight='600'
            textTransform='capitalize'
            truncate
          >
            {authorQuery.isLoading
              ? 'Loading…'
              : authorQuery.isError
                ? 'Error'
                : username()}
          </Text>
          <Text
            as='span'
            fontSize='9px'
            fontWeight='600'
            letterSpacing='.1em'
            textTransform='uppercase'
            lineHeight='1.3'
            px='6px'
            py='2px'
            borderRadius='5px'
            style={{
              color: rankColor(rank()),
              border: `1px solid ${rankColor(rank())}66`,
            }}
          >
            {rank().replaceAll('_', ' ')}
          </Text>
        </Flex>
        <Show
          when={!props.metaSlot}
          fallback={props.metaSlot}
        >
          <Text
            color="editorialInk3"
            fontSize={props.dateFontSize || '11px'}
            mt='1px'
            class={css({ fontVariantNumeric: 'tabular-nums' })}
          >
            {dayjs(props.created_at).format('DD MMMM, YYYY')}
          </Text>
        </Show>
      </Grid>
    </Flex>
  )
}
